import {
  query,
  orderBy,
  CollectionReference,
  DocumentData,
  collection,
  where,
  Query,
  QuerySnapshot,
  getDocs,
  doc,
  Firestore,
  getDoc,
} from "firebase/firestore";
import { useState, useEffect, useContext } from "react";
import UserContext from "../../contexts/UserContext";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

/**
 * A type representing information for creating a leaderboard
 */
type LeaderboardProps = {
  mode: string; // "Duels" | "Iron Sights";
  metric: string; // "Precision" | "Accuracy";
  difficulty: string; // "Easy" | "Medium" | "Hard";
};

/**
 * A type representing information stored in a row of a leaderboard
 */
type LeaderboardEntry = {
  username: string; // currently the uID is used for this entry; in the future it will be the username from the account document
  score: number;
};

/**
 * Renders a leaderboard for the "/leaderboards" page
 * @returns html containing ...
 */
const Leaderboard = (props: LeaderboardProps) => {
  const [leaderboardRows, setleaderboardRows] = useState<LeaderboardEntry[]>(
    []
  ); //Represents the entries/rows of this leaderboard
  const userContext = useContext(UserContext);
  const [loading, setLoading] = useState(true); // whether the leaderboard is currently loading

  const makeLeaderboard = async () => {
    if (!userContext.db) return;

    let db: Firestore = userContext.db;

    const topN = 10; //top 10 will be considered for a leaderboard placement

    const roundsRef: CollectionReference<DocumentData> = collection(
      db,
      "rounds"
    );

    const q: Query<DocumentData> = query(
      roundsRef,
      where("type", "==", props.mode),
      where("difficulty", "==", props.difficulty),
      orderBy(
        props.metric.toLowerCase(),
        props.metric === "Accuracy" ? "asc" : "desc"
      )
    );

    const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(q);

    let highScoreUsers: Set<string> = new Set();

    let leaderboardRows: LeaderboardEntry[] = [];

    let uniqueUsers = 0; //need to decide if we want top 10 USERS or top 10 SCORES on the leaderboard.
    //Right now we have top 10 USERS

    for (const docu of querySnapshot.docs) {
      // doc.data() is never undefined for query doc snapshots
      //https://stackoverflow.com/questions/37576685/using-async-await-with-a-foreach-loop
      let uID: string = docu.data().userID;

      if (!highScoreUsers.has(uID) && uniqueUsers < topN) {
        highScoreUsers.add(uID);
        uniqueUsers++;

        let score: number = docu.data()[props.metric.toLowerCase()];

        //get the username corresponding to the uID
        let userRef = doc(db, "users", uID);

        let userAccount = await getDoc(userRef);

        let uName: string =
          userAccount.exists() && userAccount.data().loginUsername
            ? userAccount.data().loginUsername
            : "Anonymous";

        leaderboardRows.push({ username: uName, score });
      }
    }

    setleaderboardRows(leaderboardRows);
    setLoading(false);
  };

  /**
   * If loading is true (and userContext.db doesn't equal null), try to make the leaderboard.
   */
  useEffect(() => {
    if (loading) makeLeaderboard();
  }, [loading, userContext.db]);

  return (
    <>
      {!loading ? (
        <div
          className="bg-indigo-500 rounded-md px-4 pb-4 pt-6 md:mx-10 h-min"
          style={{ width: "30rem" }}
        >
          <p className="font-semibold text-4xl">{props.mode}</p>
          <p className="text-2xl pb-4">{props.difficulty}</p>
          <div className="bg-white rounded-md px-4 py-2">
            <div className="flex justify-between">
              <p className="text-2xl">Username</p>
              <p className="text-2xl">Score</p>
            </div>
            <div className="bg-gray-800 h-0.5 my-2" />
            {leaderboardRows.map((data, i) => (
              <div className="flex justify-between" key={i}>
                <p className="text-2xl">{data.username}</p>
                <p className="text-2xl">{data.score}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-md mx-10" style={{ width: "32rem" }}>
          <Skeleton height={500} />{" "}
          {/** React Skeleton to display that a leaderboard is loading */}
        </div>
      )}
    </>
  );
};

export default Leaderboard;
